<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn icon title="Back to previous page" dark @click="$router.push('/company')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-menu left v-model="menu_status" offset-y > 
                        <template v-slot:activator="{ on }">
                            <v-btn color="primary" dark v-on="on">
                                &nbsp;&nbsp;CY {{selectedYear}}&nbsp;&nbsp;
                                <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile v-for="(item,index) in fiscal_years" :key="index" :title="`Calendar Year ${item.fiscalYear}`" @click="setSelectedYear(item.fiscalYear)">
                                <v-list-tile-title small>CY {{item.fiscalYear}}</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </v-toolbar>
            </v-card>
            
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                {{errorMessage}}
            </v-alert>

            <!-- Account Details -->
            <v-card class="elavation-1 mt-5">
                <v-toolbar flat height="45px">
                    <b class="primary--text">ACCOUNT DETAILS</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-card-text>
                    <v-layout row wrap> 
                        <v-flex xs12 sm6>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Company Name:
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{ account.companyName }}
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'"> 
                                <v-flex xs4 class="text-xs-left">
                                    Account Plan :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{account.accountName}}
                                </v-flex>  
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'"> 
                                <v-flex xs4 class="text-xs-left">
                                    Account Start Date :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">      
                                    {{account.accountStartDate | moment('MMMM DD, YYYY') }}
                                </v-flex>
                            </v-layout>
                            <v-layout v-if="isTrialAccount" row wrap :class="isMobile?'section-layout-mobile':'section-layout'"> 
                                <v-flex xs4 class="text-xs-left">
                                    Trial Expiration :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">      
                                    {{account.expirationDate | moment('MMMM DD, YYYY') }}
                                </v-flex>
                            </v-layout>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'"> 
                                <v-flex xs4 class="text-xs-left">
                                    Account Number :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{account.accountNumber}}
                                </v-flex>  
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'"> 
                                <v-flex xs4 class="text-xs-left">
                                    Employees :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{account.activeEmployees}} Active, {{account.inactiveEmployees}} Inactive
                                </v-flex>  
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Billing Type :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    <span v-if="account.billingType==1">Monthly</span>
                                    <span v-else>Annual</span>
                                </v-flex>
                            </v-layout>
                            <v-layout v-if="isTrialAccount" :class="isMobile?'section-layout-mobile':'section-layout'"> 
                                <v-flex xs4 class="text-xs-left">
                                    Trial Remaining Days :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">      
                                    <span v-if="account.remainingTrialDays>0">{{account.remainingTrialDays}} days</span>
                                    <span v-else>0 days</span>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <v-alert v-if="!billing_contacts.length" :value="true" color="info" icon="info" outline>
                To complete your billing setup, please <b>add at least one Primary Billing Contact</b>. This contact will appear on your Statement of Account and Invoices. Click the "+" button to proceed.<br>
                You may also add multiple Secondary Contacts. All secondary contacts will be copied (CC) on all billing-related emails sent by SweldoNow.
            </v-alert>
            
            <!-- Billing Contacts -->
            <v-card class="elavation-1 mt-5">
                <v-toolbar flat height="45px">
                    <b class="primary--text">BILLING CONTACTS</b>
                    <v-spacer></v-spacer>
                    <v-btn v-if="hasEditRights" fab small class="primary" @click="addContact" title="Add Billing Contact">
                        <v-icon>add</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="billing_contacts"
                    :loading="loading"
                    :hide-actions="billing_contacts.length<=5"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="editContact(props.item)">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container">
                                            <v-avatar size="30">
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.user.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.user.firstName}} {{props.item.user.lastName}}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                        <b v-if="props.item.primary">Primary</b>
                                        <span v-else>Secondary</span>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="card-layout-mobile">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs12>
                                                    <div class="caption grey--text text--darken-1">Address</div>
                                                    <div>{{props.item.address}}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Mobile</div>
                                                    <div v-if="props.item.user.phone">+{{props.item.user.phone}}</div>
                                                </v-flex>
                                                <v-flex xs8>
                                                    <div class="caption grey--text text--darken-1">Email</div>
                                                    <div>{{props.item.user.email}}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="editContact(props.item)" :class="hasEditRights?'clickable':''" :title="hasEditRights?'Click to edit':''">
                            <td>
                                <b v-if="props.item.primary">Primary</b>
                                <span v-else>Secondary</span>
                            </td>
                            <td>
                                <v-chip class="user-chip-container">
                                    <v-avatar size="30">
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.user.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.user.firstName}} {{props.item.user.lastName}}
                                </v-chip>
                            </td>
                            <td>
                                {{props.item.address}}
                            </td>
                            <td>
                                <span v-if="props.item.user.phone">+{{props.item.user.phone}}</span>
                            </td>
                            <td>
                                {{props.item.user.email}}
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>

            <!-- Billing Details -->
            <v-card class="elavation-1 mt-5">
                <v-toolbar flat height="45px">
                    <b class="primary--text">BILLING LIST</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : header_billing"
                    :items="billing_list_filtered" 
                    :must-sort="false"
                    :disable-initial-sort="true"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="viewBillingDetails(props.item)">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container">
                                            {{ props.item.billingStartDate | moment("MMM D") }} - {{ props.item.billingEndDate | moment("D, YYYY") }}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="card-layout-mobile">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Due Date</div>
                                                    <div>{{ props.item.dueDate | moment("MMM D, YYYY") }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Status</div>
                                                    <div :class="commonfn.getStatusColor(props.item.status)">{{ commonfn.getStatusDescription(props.item.status) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">SOA Number</div>
                                                    <div>{{ props.item.invoiceNumber }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Amount Due</div>
                                                    <div v-if="props.item.invoiceNumber">₱{{ numformat.formatNumber(props.item.amountDue) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Amount Paid</div>
                                                    <div v-if="props.item.amountPaid>0">₱{{ numformat.formatNumber(props.item.amountPaid) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Paid On</div>
                                                    <div>{{ props.item.paidOn | moment("MMM D, YYYY") }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout v-if="props.item.orNumber" row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">OR Number</div>
                                                    <div>{{ props.item.orNumber }}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="viewBillingDetails(props.item)" :class="isDraft(props.item)?'':'clickable'" :title="isDraft(props.item)?'':'Click to view details'">
                            <td>{{ props.item.billingStartDate | moment("MMM D") }} - {{ props.item.billingEndDate | moment("D, YYYY") }}</td>
                            <td>{{ props.item.dueDate | moment("MMM D, YYYY") }}</td>
                            <td>
                                <span :class="commonfn.getStatusColor(props.item.status)">{{ commonfn.getStatusDescription(props.item.status) }}</span>
                            </td>
                            <td>{{ props.item.invoiceNumber }}</td>
                            <td>
                                <span v-if="props.item.invoiceNumber">₱{{ numformat.formatNumber(props.item.amountDue) }}</span>
                            </td>
                            <td>
                                <span v-if="props.item.amountPaid>0">₱{{ numformat.formatNumber(props.item.amountPaid) }}</span>
                            </td>
                            <td>{{ props.item.paidOn | moment("MMM D, YYYY") }}</td>
                            <td>{{ props.item.orNumber }}</td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>

        <v-dialog v-model="dialogAddEditContact" persistent width="500">
            <add-edit-contact ref="refContactComp" :dialogAddEditContact.sync="dialogAddEditContact" @send="getContacts"></add-edit-contact>
        </v-dialog>
    </v-layout>
</template>
<script>

import commonfn from '@/common/functions'   //for refactor
import billingApi from '@/api/billing'
import accountTypeKeys from '@/common/accountTypeKeys'
import numformat from '@/common/num_format'
import awsS3Helper from '@/helpers/aws-s3'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import { hasEditRights } from '@/helpers/access-permissions'

export default {
    name: 'Billing',
    metaInfo: {
        title: 'Billing',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Billing page' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'add-edit-contact': ()=> import("@/components/Billing/AddEditBillingContact"),
    },
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            commonfn,
            numformat,
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            selectedYear: userPrefs.getCalendarYear(this.$route.name),
            error: false,
            errorMessage: '',
            loading: false,
            disabled: false,
            dialogAddEditContact: false,
            menu_status: false,
            headers: [
                {text:'Type', align:'left', value:'', width: '5%', sortable:false},
                {text:'Employee', align:'left', value:'firstName', width:'25%', sortable:false},
                {text:'Address', align:'left', value:'', width:'40%', sortable:false},
                {text:'Mobile No.', align:'left', value:'', width:'15%', sortable:false},
                {text:'Email', align:'left', value:'', width: '15%', sortable:false},
            ],
            header_billing: [
                {text:'Billing Period', align: 'left', value:'billingStartDate', width:'10%', sortable:true},
                {text:'Due Date', align: 'left', value:'dueDate',  width:'10%', sortable:true},
                {text:'Status', align: 'left', value:'status',  width:'12%', sortable:true},
                {text:'SOA Number',align: 'left', value:'invoiceNumber',  width:'10%', sortable:true},
                {text:'Amount Due', align: 'left', value:'amountDue',  width:'10%', sortable:true},
                {text:'Amount Paid', align: 'left', value:'amountPaid',  width:'10%', sortable:true},
                {text:'Paid On', align: 'left', value:'paidOn',  width:'10%', sortable:true},
                {text:'OR Number', align: 'left', value:'orNumber',  width:'10%', sortable:true},
            ],
            account: null,
            company: null,
            billing_contacts: [],
            billing_list: [],
        }
    },
    methods: {
        viewBillingDetails(data) {
            if (!this.isDraft(data)) {
                this.$router.push(`billing/${data.invoiceId}`)
            }
        },  
        setSelectedYear(year) {
            this.selectedYear = year
            userPrefs.updateCalendarYear(this.$route.name, year)
            this.getBilling()
        },
        isDraft(data) {
            return commonfn.getStatusDescription(data.status) == 'Draft' || 
                commonfn.getStatusDescription(data.status) == 'Not Available' || 
                commonfn.getStatusDescription(data.status) == 'Pending'
        },
        async addContact() {
            this.$refs.refContactComp.setAdd()
            this.dialogAddEditContact = true
        },
        async editContact(data) {
            this.$refs.refContactComp.setEdit(data, this.billing_contacts.length)
            this.dialogAddEditContact = true
        },
        async getContacts() {
            try {
                this.loading = true
                const response = await billingApi.getContacts()
                if (response && response.status == 200) {
                    this.billing_contacts = response.data.contacts
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getBilling() {
            try {
                this.loading = true
                const response = await billingApi.getBillingByCompany(this.companyId, this.selectedYear)                
                if (response && response.status == 200) {
                    this.account = response.data.account
                    this.billing_contacts = response.data.contacts
                    this.billing_list = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Company Billing'
        })

        this.getBilling()
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        companyId() {
            return this.$store.state.companyId
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        isTrialAccount() {
            return this.account.accountKey == accountTypeKeys.Trial
        },
        isMobile() {
            return window.innerWidth < 600
        },
        billing_list_filtered() {
            var temp = this.billing_list.filter(item => {
                return (this.$moment(item.billingStartDate).format('YYYY') == this.selectedYear)
            })
            return temp
        },
        fiscal_years() {
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: new Date().getFullYear()}]
        }
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 20px 0px 50px;
}
.section-layout-mobile {
    padding:0px 10px 0px 10px;
}
.clickable {
    cursor:pointer;
}
.card-layout-mobile {
    padding:0px 23px 0px 23px;
}
</style>>